import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Calendar, Eye, Tag, Search } from 'lucide-react';
import { Button } from '../components/ui/button';
import SEO from '../components/SEO';
import { Input } from '../components/ui/input';
import { API_URL, formatDate, truncateText } from '../lib/utils';

const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.5 }
};

// Placeholder blog posts for initial display
const placeholderPosts = [
  {
    id: '1',
    title: 'Rentrée académique 2024-2025 : Ce qu\'il faut savoir',
    slug: 'rentree-academique-2024-2025',
    excerpt: 'Découvrez les dates clés, les nouveautés pédagogiques et les conseils pour bien démarrer votre année académique à CEFAT Business School.',
    content: '',
    cover_image: 'https://images.unsplash.com/photo-1523050854058-8df90110c9f1?w=600&h=400&fit=crop',
    author: 'CEFAT Admin',
    category: 'Actualités',
    tags: ['rentrée', '2024', 'académique'],
    published: true,
    views: 245,
    created_at: '2024-11-15T10:00:00Z'
  },
  {
    id: '2',
    title: 'Partenariat renforcé avec le Bureau International du Travail',
    slug: 'partenariat-bit-2024',
    excerpt: 'CEFAT Business School consolide son partenariat avec le BIT pour offrir des certifications reconnues internationalement.',
    content: '',
    cover_image: 'https://images.unsplash.com/photo-1521737711867-e3b97375f902?w=600&h=400&fit=crop',
    author: 'Direction',
    category: 'Partenariats',
    tags: ['BIT', 'partenariat', 'certification'],
    published: true,
    views: 189,
    created_at: '2024-11-10T14:30:00Z'
  },
  {
    id: '3',
    title: 'Cérémonie de remise des diplômes 2024',
    slug: 'ceremonie-diplomes-2024',
    excerpt: 'Plus de 500 étudiants ont reçu leurs diplômes lors de la cérémonie annuelle de CEFAT Business School.',
    content: '',
    cover_image: 'https://images.unsplash.com/photo-1627556704302-624286467c65?w=600&h=400&fit=crop',
    author: 'Communication',
    category: 'Événements',
    tags: ['diplômes', 'cérémonie', 'graduation'],
    published: true,
    views: 312,
    created_at: '2024-11-05T09:00:00Z'
  }
];

export default function Blog() {
  const [posts, setPosts] = useState(placeholderPosts);
  const [search, setSearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPosts();
  }, []);

  const fetchPosts = async () => {
    try {
      const response = await fetch(`${API_URL}/blogs?published_only=true`);
      if (response.ok) {
        const data = await response.json();
        if (data.length > 0) {
          setPosts(data);
        }
      }
    } catch (error) {
      console.log('Using placeholder posts');
    } finally {
      setLoading(false);
    }
  };

  const categories = [...new Set(posts.map(p => p.category))];

  const filteredPosts = posts.filter(post => {
    const matchesSearch = post.title.toLowerCase().includes(search.toLowerCase()) ||
                         post.excerpt.toLowerCase().includes(search.toLowerCase());
    const matchesCategory = !selectedCategory || post.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  return (
    <div data-testid="blog-page">
      <SEO
        title="Blog & Actualites - CEFAT Business School"
        description="Blog CEFAT Business School : actualites, evenements, conseils carriere, partenariats. Restez informe sur la vie academique et les opportunites a Abidjan."
        keywords="blog CEFAT, actualites ecole commerce Abidjan, evenements CEFAT, vie etudiante Abidjan, formation Cote d'Ivoire actualites"
        url="/blog"
      />
      {/* Hero */}
      <section className="bg-[#003366] py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <motion.h1 
            {...fadeInUp}
            className="font-heading text-4xl md:text-5xl font-bold text-white mb-4"
          >
            Blog & Actualités
          </motion.h1>
          <motion.p 
            {...fadeInUp}
            className="text-white/80 text-lg max-w-2xl mx-auto"
          >
            Découvrez les dernières nouvelles, événements et actualités de CEFAT Business School.
          </motion.p>
        </div>
      </section>

      {/* Filters */}
      <section className="py-8 bg-white border-b">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
            <div className="relative w-full md:w-96">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
              <Input
                type="text"
                placeholder="Rechercher un article..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10 h-12 border-gray-200"
                data-testid="blog-search"
              />
            </div>
            <div className="flex gap-2 flex-wrap">
              <Button
                variant={!selectedCategory ? "default" : "outline"}
                onClick={() => setSelectedCategory('')}
                className={`rounded-full ${!selectedCategory ? 'bg-[#003366]' : ''}`}
              >
                Tous
              </Button>
              {categories.map(cat => (
                <Button
                  key={cat}
                  variant={selectedCategory === cat ? "default" : "outline"}
                  onClick={() => setSelectedCategory(cat)}
                  className={`rounded-full ${selectedCategory === cat ? 'bg-[#003366]' : ''}`}
                >
                  {cat}
                </Button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Posts Grid */}
      <section className="py-16 bg-[#F8FAFC]">
        <div className="max-w-7xl mx-auto px-4">
          {filteredPosts.length === 0 ? (
            <div className="text-center py-16">
              <p className="text-gray-500 text-lg">Aucun article trouvé.</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredPosts.map((post, index) => (
                <motion.article
                  key={post.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-white rounded-xl overflow-hidden shadow-md card-hover"
                >
                  <div className="relative h-48 overflow-hidden">
                    {post.cover_image ? (
                      <img 
                        src={post.cover_image} 
                        alt={post.title}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-[#003366] to-[#001a33]" />
                    )}
                    <div className="absolute top-4 left-4">
                      <span className="badge badge-blue">{post.category}</span>
                    </div>
                  </div>
                  <div className="p-6">
                    <h2 className="font-heading text-xl font-bold text-[#003366] mb-3 line-clamp-2">
                      {post.title}
                    </h2>
                    <p className="text-gray-600 mb-4 line-clamp-3">
                      {truncateText(post.excerpt, 120)}
                    </p>
                    <div className="flex items-center justify-between text-sm text-gray-500 mb-4">
                      <span className="flex items-center gap-1">
                        <Calendar size={14} /> {formatDate(post.created_at)}
                      </span>
                      <span className="flex items-center gap-1">
                        <Eye size={14} /> {post.views} vues
                      </span>
                    </div>
                    <Link to={`/blog/${post.slug}`}>
                      <Button variant="outline" className="w-full border-[#003366] text-[#003366] hover:bg-[#003366] hover:text-white rounded-full">
                        Lire la suite <ArrowRight size={16} className="ml-2" />
                      </Button>
                    </Link>
                  </div>
                </motion.article>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Newsletter CTA */}
      <section className="py-16 bg-[#003366]">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="font-heading text-3xl font-bold text-white mb-4">
            Restez informé
          </h2>
          <p className="text-white/80 mb-8">
            Suivez nos actualités et ne manquez aucune opportunité de formation.
          </p>
          <Link to="/contact">
            <Button className="bg-[#DC143C] hover:bg-[#B01030] text-white rounded-full px-8 py-3 font-semibold">
              Contactez-nous <ArrowRight className="ml-2" size={18} />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
